<template>
  <div class="statistics-chart">
    <div v-if="showTypeSelector" class="statistics-chart__type-selector">
      <label class="statistics-chart__type-label">Tipo de gráfico</label>
      <div class="chart-type-group">
        <button
          v-for="opt in chartTypeOptions"
          :key="opt.value"
          type="button"
          :class="['chart-type-btn', { active: chartType === opt.value }]"
          @click="selectChartType(opt.value)"
        >
          <i :class="opt.icon"></i>
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div class="statistics-chart__panel">
      <b-spinner v-if="loading" />

      <apexchart
        v-if="!loading && chartOptions && hasData"
        :key="chartRenderKey"
        :height="chartHeight"
        :type="apexChartType"
        :options="chartOptions"
        :series="series"
      />

      <p v-if="!loading && showEmptyState" class="statistics-chart__empty">
        {{ emptyMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { CHART_TYPE_OPTIONS, getSelectedClassKeys } from '@/utils/defectStatistics'
import { buildStatisticsChart } from '@/utils/statisticsChartBuilder'

export default {
  name: 'StatisticsChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    noData: {
      type: Boolean,
      default: false
    },
    chartType: {
      type: String,
      default: 'bar'
    },
    rawData: {
      type: Object,
      default: null
    },
    chartClasses: {
      type: Array,
      default: () => []
    },
    columnList: {
      type: Array,
      default: () => []
    },
    selectedClasses: {
      type: Array,
      default: () => []
    },
    groupBy: {
      type: String,
      default: 'month'
    },
    showTypeSelector: {
      type: Boolean,
      default: true
    },
    emptyMessage: {
      type: String,
      default: 'Nenhum dado de análise encontrado para o período selecionado.'
    },
    chartTypeOptions: {
      type: Array,
      default: () => CHART_TYPE_OPTIONS
    }
  },
  data () {
    return {
      chartOptions: null,
      series: [],
      hasData: false,
      chartRenderKey: 0
    }
  },
  computed: {
    apexChartType () {
      return this.chartType === 'boxPlot' ? 'boxPlot' : this.chartType
    },
    chartHeight () {
      return this.chartType === 'boxPlot' ? 420 : 350
    },
    showEmptyState () {
      return this.noData || !this.hasData
    }
  },
  watch: {
    chartType: 'rebuildChart',
    rawData: { handler: 'rebuildChart', deep: true },
    chartClasses: { handler: 'rebuildChart', deep: true },
    columnList: { handler: 'rebuildChart', deep: true },
    selectedClasses: { handler: 'rebuildChart', deep: true },
    groupBy: 'rebuildChart'
  },
  mounted () {
    this.rebuildChart()
  },
  methods: {
    selectChartType (type) {
      if (type !== this.chartType) {
        this.$emit('update:chartType', type)
        this.$emit('chart-type-change', type)
      }
    },
    rebuildChart () {
      if (!this.rawData || !this.chartClasses.length || !this.columnList.length) {
        this.chartOptions = null
        this.series = []
        this.hasData = false
        return
      }

      const selectedKeys = getSelectedClassKeys(this.chartClasses, this.selectedClasses)
      const { options, series, hasData } = buildStatisticsChart({
        chartType: this.chartType,
        selectedKeys,
        columnList: this.columnList,
        data: this.rawData,
        groupBy: this.groupBy
      })

      this.chartOptions = options
      this.series = series
      this.hasData = hasData
      this.chartRenderKey += 1
    }
  }
}
</script>

<style scoped>
.statistics-chart {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.statistics-chart__type-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.statistics-chart__type-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1rem;
  margin: 0;
}

.chart-type-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chart-type-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #ffffff;
  color: #6c757d;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
  white-space: nowrap;
}

.chart-type-btn:hover {
  border-color: #FF8C42;
  color: #FF8C42;
}

.chart-type-btn.active {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border-color: #FF8C42;
  color: white;
}

.chart-type-btn.active i {
  color: white;
}

.statistics-chart__panel {
  width: 100%;
}

.statistics-chart__empty {
  text-align: center;
  color: #6c757d;
  font-size: 1rem;
  padding: 30px 10px;
  margin: 0;
}

@media (max-width: 768px) {
  .chart-type-group {
    width: 100%;
  }

  .chart-type-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
